import React, { useEffect, useState } from 'react';
import { BaseControlProps } from '../../models/BaseControlProps';
import { UserAPIService } from '../../services/UserAPIService';
import { useNavigate } from 'react-router-dom';
import { DataTable, Checkbox, Pager, SquareButton, CheckboxChange, Span } from '@tag/tag-components-react-v4';
import { UserGetRequest } from '../../models/UserGetRequest';
import { UserDeleteRequest } from '../../models/UserDeleteRequest';
import { User } from '../../models/User';
import { useAuth } from '../../services/AuthContext';
import { UtilService } from '../../services/UtilService';
import { UserEnableDisableRequest } from '../../models/UserEnableDisableRequest';
import { UserResetPasswordRequest } from '../../models/UserResetPasswordRequest';
import { useMessage } from '../../components/Common/MessageContext/MessageContext';
import { Autocomplete, Box, Button, Card, CardContent, Divider, FormControl, Grid, IconButton, InputLabel, Menu, MenuItem, Select, SelectChangeEvent, Skeleton, TextField, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import ConfirmPrompt from '../../components/Common/ConfirmPrompt/ConfirmPrompt';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendMessage from '../../components/SendMessage/SendMessage';
import HistoryIcon from '@mui/icons-material/History';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { PensionScheme } from '../../models/PensionScheme';
import { PayGroup } from '../../models/PayGroup';
import { Account } from '../../models/Account';
import { UserUnlockAccountRequest } from '../../models/UserUnlockAccountRequest';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
        },
    },
};

const ManagePensioners = (props: BaseControlProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const pageSize = 15;
    const [page, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [scopeGet, applyGet] = useState<UserGetRequest>({
        userId: 0,
        pageSize: pageSize,
        pageNo: 0,
        filterSearchString: "",
        orderByString: ""
    });
    const [scopeDelete, applyDelete] = useState<UserDeleteRequest>({
        portaluserIds: ''
    });
    const [scopeEnableDisable, applyEnableDisable] = useState<UserEnableDisableRequest>({
        userId: 0,
        portalUserIds: '0'
    });
    const [scopeResetPassword, applyResetPassword] = useState<UserResetPasswordRequest>({
        portalUserId: ''
    });
    const [scopeUnlockAccount, applyUnlockAccount] = useState<UserUnlockAccountRequest>({
        portalUserId: ''
    });
    const [sendMessageModalVisible, setSendMessageModalVisible] = useState(false);
    const [pensioners, setPensioners] = useState<User[]>([]);
    const apiService = new UserAPIService();
    const authContext = useAuth();
    const { showMessage } = useMessage();
    const [ pensionerName, setPensionerName ] = useState('');
    const [ pensionerEmail, setPensionerEmail ] = useState('');
    const [ pensionerMeppNo, setPensionerMeppNo ] = useState('');
    const [ pensionerPayGroup, setPensionerPayGroup] = useState<string>('');
    const [ pensionerSchema, setPensionerSchema ] = useState<string>('');
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [loading, isLoading] = useState<boolean>(true);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState('none');
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [allSelected, setAllSelected] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [pensionSchemeList, setPensionSchemeList] = useState<PensionScheme[]>([]);
    const [payGroupList, setPayGroupList] = useState<PayGroup[]>([]);
    

    useEffect(() => {
        const getPensionersAsync = async () => {
            await getAllPensioners();
        }

        const fetchPensionSchemeAndPayGroup = async () => {
            await getPensionSchemeAndPayGroup();
        }
        
        fetchPensionSchemeAndPayGroup();
        getPensionersAsync();
    }, []);

    useEffect(() => {
        // Skip the effect if the component is initializing
        if (!isInitialized) {
            setIsInitialized(true);
            return;
        }
        
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeoutId = setTimeout(() => {
            const getPensionersAsync = async () => {
                await getAllPensioners();
            };
            
            getPensionersAsync();
        }, 900); // 500ms delay

        setSearchTimeout(timeoutId);

        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [ pensionerName, pensionerEmail, pensionerMeppNo, pensionerPayGroup, pensionerSchema]);

    const getAllPensioners = () => {
        isLoading(true);
        scopeGet.filterSearchString = getSeachFilters();
        scopeGet.orderByString = getOrderByFilters();
        applyGet(scopeGet);
        apiService.getAllPensioners(scopeGet, authContext.token).then((response) => {
            if (response.error) {
                if (props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }
                showMessage(response.error, 'error');
            }
            else {
                setPensioners(response.response?.users ?? []);
                setSelectedRows([]);
                setAllSelected(false);
                const pageCount =
                    response.response && response.response?.users.length > 0
                        ? Math.ceil(response.response.totalCount / pageSize)
                        : 0;
                setPageCount(pageCount && pageCount > 0 ? pageCount : 0);
            }
            isLoading(false);
        });
    }

    const getPensionSchemeAndPayGroup = () => {
        apiService.getPensionSchemeAndPayGroup({ userId: 0 }, authContext.token).then((response) => {
            if (response.error) {
                if (props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }
                showMessage(response.error, 'error');
            }
            else {

                if (response.response?.pensionSchemes && response.response?.pensionSchemes.length  > 0) {
                    setPensionSchemeList(response.response?.pensionSchemes);
                }

                if (response.response?.payGroups && response.response?.payGroups.length  > 0) {
                    setPayGroupList(response.response?.payGroups);
                }
            }
        })
    }

    const getOrderByFilters = () => {
        return  `{
            "OrderByFields": [
              {
                "FieldName": "createdOn",
                "OrderDirection": "DESC"
              }
            ]
        }`
    }

    const getSeachFilters = () => {
        let searchFilters = [];
        if (pensionerName.trim() !==  "") {
            searchFilters.push(`{"FieldName":"fullname","SearchType":"contains","SearchValue":"${pensionerName}"}`);
        }

        if (pensionerEmail.trim() !== "") {
            searchFilters.push(`{"FieldName":"emailAddress","SearchType":"equals","SearchValue":"${pensionerEmail}"}`);
        }

        if (pensionerMeppNo.trim() !== "") {
            searchFilters.push(`{"FieldName":"peopleXDId","SearchType":"contains","SearchValue":"${pensionerMeppNo}"}`);
        }

        if (pensionerPayGroup.trim() !== "") {
            searchFilters.push(`{"FieldName":"PayGroup","SearchType":"contains","SearchValue":"${pensionerPayGroup}"}`);
        }

        if (pensionerSchema.trim() !== "") {
            searchFilters.push(`{"FieldName":"PensionScheme","SearchType":"contains","SearchValue":"${pensionerSchema}"}`);
        }

        return searchFilters.length >= 0 ? `{"SearchFilters": [${searchFilters.join(",")}]}` : '';
    }

    const deletePensioner = () => {
        scopeDelete.portaluserIds = selectedRows.join(', ');
        applyDelete(scopeDelete);
        apiService.deleteUser(scopeDelete, authContext.token).then((response) => {
            if (response.error) {
                if (props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }

                showMessage(response.error, 'error');
            }
            else {
                showMessage('Pensioner Deleted Successfully', 'success');
                getAllPensioners();
            }

            setConfirmLoading(false);
            setConfirmVisible(false);
        })
    };

    const enableDisablePensioner = () => {
        scopeEnableDisable.portalUserIds = selectedRows.join(', ');
        scopeEnableDisable.disable = true;
        scopeEnableDisable.enable = false;
        applyEnableDisable(scopeEnableDisable);
        apiService.enableDisableUser(scopeEnableDisable, authContext.token).then((response) => {
            if (response.error) {
                if (props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }

                showMessage(response.error, 'error');
            }
            else {
                showMessage('Pensioner Enabled/Disabled Successfully', 'success');
                getAllPensioners();
            }

            setConfirmLoading(false);
            setConfirmVisible(false);
        })
    }

    const resetPassword = () => {
        scopeResetPassword.portalUserId = selectedRows.join(', ');
        applyResetPassword(scopeResetPassword);
        apiService.resetUserPassword(scopeResetPassword, authContext.token).then((response) => {
            if (response.error) {
                if (props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }

                showMessage(response.error, 'error');
            }
            else {
                showMessage('Pensioner Password Reset Successfully', 'success');
                getAllPensioners();
            }

            setConfirmLoading(false);
            setConfirmVisible(false);
        })
    }

    const unlockUserAccount = () => {
        scopeUnlockAccount.portalUserId = selectedRows.join(', ');
        applyUnlockAccount(scopeUnlockAccount);
        apiService.unlockUserAccount(scopeUnlockAccount, authContext.token).then((response) => {
            if (response.error) {
                if (props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }

                showMessage(response.error, 'error');
            }
            else {
                showMessage('Pensioner Account Unlocked Successfully', 'success');
                getAllPensioners();
            }

            setConfirmLoading(false);
            setConfirmVisible(false);
        })
    }

    // Handle header checkbox click (Select All)
    const handleSelectAll = (event: CheckboxChange) => {
        if (event.checked) {
            const allRowIds = pensioners.map((row) => row.userId);
            setSelectedRows(allRowIds); // Select all rows
            setAllSelected(true);
        } else {
            setAllSelected(false);
            setSelectedRows([]); // Deselect all rows
        }
    };

    // Handle individual row checkbox click
    const handleRowSelect = (userId: number) => {
        setSelectedRows((prevSelected) =>
            prevSelected.includes(userId)
                ? prevSelected.filter((id) => id !== userId) // Deselect
                : [...prevSelected, userId] // Select
        );
    };
    
    function renderDisableButton(userId: number) {
        return (
            <Tooltip title="Disable/Enable Pensioner">
                <IconButton
                    aria-label='disable-enable'
                    size='small'
                    color='warning'
                    onClick={() => {
                        setConfirmTitle('Disable/Enable Pensioner');
                        setConfirmMessage("Are you sure you want to enable/disable this Pensioner?");
                        handleRowSelect(userId);
                        showConfirmation('disable')
                    }}
                >
                    <BlockIcon />
                </IconButton>
            </Tooltip>
        );
    }

    function renderDeleteButton(userId: number) {
        return (
            <Tooltip title="Delete Pensioner">
                <IconButton
                    aria-label='delete-pensioner'
                    size='small'
                    color='error'
                    onClick={() => {
                        setConfirmTitle('Delete Pensioner');
                        setConfirmMessage("Are you sure you want to delete this Pensioner?");
                        handleRowSelect(userId);
                        showConfirmation('delete')
                    }}
                >
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        );
    }

    function renderResetPasswordButton(userId: number) {
        return (
            <Tooltip title="Reset Pensioner Password">
                <IconButton
                    aria-label='reset-password'
                    size='small'
                    color='default'
                    onClick={() => {
                        setConfirmTitle('Reset Pensioner Password');
                        setConfirmMessage("Are you sure you want to reset this Pensioner's password ?");
                        handleRowSelect(userId);
                        showConfirmation('reset');
                    }}
                >
                    <LockResetIcon />
                </IconButton>
            </Tooltip>
        );
    }

    function renderUnlockAccountButton(userId: number) {
        return (
            <Tooltip title="Unlock Pensioner Account">
                <IconButton
                    aria-label='unlock-account'
                    size='small'
                    color='success'
                    onClick={() => {
                        setConfirmTitle('Unlock Pensioner Account');
                        setConfirmMessage("Are you sure you want to unlock this Pensioner account ?");
                        handleRowSelect(userId);
                        showConfirmation('unlock');
                    }}
                >
                    <LockOpenIcon />
                </IconButton>
            </Tooltip>
        );
    }

    function renderHeaderSelect() {
        setAllSelected(allSelected);
        return (
            <Checkbox 
                checked={allSelected}
                onChange={handleSelectAll}
            />
        );
    }

    function renderRowSelect({ row }: any) {
        const isSelected = selectedRows.includes(row.original.userId) || allSelected;
        return (
            <Checkbox 
                checked={isSelected}
                onChange={() => handleRowSelect(row.original.userId)}
            />
        );
    }

    function renderMessagesButton() {
        return (
            <Tooltip title="Send Message to Pensioner">
                <IconButton
                    aria-label='send-message'
                    size='small'
                    color='primary'
                    onClick={() => setSendMessageModalVisible(true)}
                >
                    <MailOutlineIcon />
                </IconButton>
            </Tooltip>
        );
    }

    function renderLoginActivityButton(emailAddress: string) {
        return (
            <Tooltip title="View Login Activities">
                <IconButton
                    aria-label='login-activity'
                    size='small'
                    color='info'
                    onClick={() => navigate('/login-activities?emailaddress='+emailAddress)}
                >
                    <HistoryIcon />
                </IconButton>
            </Tooltip>
        );
    }

    function renderActionButtons(user: User) {
        return (
            <div style={{ display: 'flex', justifyContent: 'right', gap: '8px' }}>
                {user.locked ? renderUnlockAccountButton(user.userId) : ''}
                {renderDisableButton(user.userId)}
                {renderDeleteButton(user.userId)}
                {renderResetPasswordButton(user.userId)}
                {/* {renderMessagesButton()} */}
                {renderLoginActivityButton(user.emailAddress)}
            </div>
        );
    }

    const resetFilters = () => {
        setPensionerName('');
        setPensionerEmail('');
        setPensionerMeppNo('');
        setPensionerPayGroup('');
        setPensionerSchema('');
    }

    const columns = React.useMemo(
        () => [
        { accessorKey: 'userId', header: renderHeaderSelect, cell: renderRowSelect },    
        { 
            accessorKey: 'emailAddress', 
            header: 'Email / Username', 
            cell: ({ row }: any) => {
                return row.original.emailAddress.trim() === '' ? row.original.userName : row.original.emailAddress;
            }
        },
        {  
            header: 'MEPP No.',
            cell: ({ row }: any) => {
                return row.original.peopleXDId;
            }
        },
        {
            header: 'Fullname',
            cell: ({ row }: any) => {
                return getUserFullName(row.original);
            },
        },
        {
            header: 'Pay Group',
            cell: ({ row }: any) => {
                const schemes = row.original.accounts && row.original.accounts.length > 0 ? row.original.accounts.map((account: Account) => account.payGroup).join(", ") : "-";
                return schemes;
            },
        },
        { 
            header: 'Scheme',                
            cell: ({ row }: any) => {
                const schemes = row.original.accounts && row.original.accounts.length > 0 ? row.original.accounts.map((account: Account) => account.pensionScheme).join(", ") : "-";
                return schemes;
            },  
        },
        { accessorKey: 'enabled', header: 'Enabled', meta: { dataType: 'boolean' } },
        { accessorKey: 'locked', header: 'Locked', meta: { dataType: 'boolean' } },
        {
            accessorKey: 'actions',
            header: () => <div style={{ textAlign: 'center' }}>Actions</div>,
            cell: ({ row} : any) => {
                return renderActionButtons(row.original);
            },
            meta: {
                getCellProps: () => {
                    return { style: { textAlign: 'center', width: '120px' } };
                },
            },
        }
        ],
        [allSelected, selectedRows],
    );

    const getUserFullName = (user: User) => {
        const fullname = user.forename + ' ' + user.surname;
        return fullname.trim() === '' ? user.userName : fullname; 
    }
    
    const handlePageChange = async (e: any) => {
        setPage(e.page - 1);
        scopeGet.pageNo = e.page - 1;
        applyGet(scopeGet);

        await getAllPensioners();
    };

    const handleConfirmCancel = () => {
        setConfirmVisible(false);
    }

    const handleConfirmConfirmed = async () => {
        setConfirmLoading(true);

        if (confirmAction === 'disable') {
            await enableDisablePensioner();
        }
        else if (confirmAction === 'delete') {
            await deletePensioner();
        }
        else if (confirmAction === 'reset') {
            await resetPassword();
        }
        else if (confirmAction === 'unlock') {
            await unlockUserAccount();
        }
    }

    const showConfirmation = (action: string) => {
        setConfirmAction(action);
        setConfirmVisible(true);
    }

    const renderheaderView = () => {
        return (
            <Box sx={{ width: '100%', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" gutterBottom>
                        Manage Pensioners
                    </Typography>
                    <Button 
                        variant='outlined'  
                        onClick={() => { setSendMessageModalVisible(true) }} 
                        startIcon={<SendIcon/>}
                        sx={{ alignSelf: 'flex-start' }}    
                    >
                        Send Messages
                    </Button>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} alignItems="center" >
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Pensioner Name"
                            variant='outlined'
                            value={pensionerName}
                            onChange={(e) => setPensionerName(e.target.value )}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Email Address"
                            variant='outlined'
                            value={pensionerEmail}
                            onChange={(e) => setPensionerEmail(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="MEPP No."
                            variant='outlined'
                            value={pensionerMeppNo}
                            onChange={(e) => setPensionerMeppNo(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>Pay Group</InputLabel>
                            <Select
                                label="Pay Group"
                                onChange={(e: SelectChangeEvent) => setPensionerPayGroup(e.target.value)}
                                MenuProps={MenuProps}
                                value={pensionerPayGroup}
                            >
                                <MenuItem selected={true} value="">Select a Pay Group</MenuItem>
                                {payGroupList.map((payGroup) => (
                                    <MenuItem selected={payGroup.payGroupName == pensionerPayGroup} key={payGroup.payGroupCode} value={payGroup.payGroupCode}>{payGroup.payGroupCode}</MenuItem>
                                ))}
                            </Select>
                        </FormControl> 
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth variant="outlined">
                            <Autocomplete
                                fullWidth
                                options={pensionSchemeList}
                                getOptionLabel={(scheme: PensionScheme) => scheme.pensionSchemeName}
                                value={pensionSchemeList.find(scheme => scheme.pensionSchemeCode === pensionerSchema) || null}
                                onChange={(event: any, newValue: PensionScheme | null) => {
                                    setPensionerSchema(newValue ? newValue.pensionSchemeCode : ""); 
                                }}
                                renderInput={(params) => (
                                    <TextField
                                    {...params}
                                    label="Pension Scheme"
                                    variant="outlined"
                                    />
                                )}
                                renderOption={(props, scheme) => (
                                    <li {...props} key={scheme.pensionSchemeCode}>
                                    {scheme.pensionSchemeName}
                                    </li>
                                )}
                                isOptionEqualToValue={(option, value) => 
                                    option.pensionSchemeCode === value.pensionSchemeCode
                                }
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            style={{ minWidth: '80px' }}
                            onClick={() => {
                                setPage(0);
                                scopeGet.pageNo = 0;
                                getAllPensioners();
                            }}
                        >
                            Filter
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="large"
                            style={{ minWidth: '80px' }}
                            onClick={() => {
                                setPage(0);
                                scopeGet.pageNo = 0;
                                resetFilters();
                            }}
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Grid container spacing={2} justifyContent="flex-start">
                        <Grid item xs={12} sm="auto">
                            <Button 
                                disabled={selectedRows.length === 0}
                                aria-label='enable-disable'
                                color='primary'
                                startIcon={<BlockIcon/>}
                                onClick={() => {
                                    setConfirmTitle('Disable/Enable Pensioner');
                                    setConfirmMessage("Are you sure you want to enable/disable all these Pensioner's?");
                                    showConfirmation('disable')
                                }}
                            >
                                Disable / Enable
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm="auto">
                            <Button 
                                disabled={selectedRows.length === 0}
                                aria-label='reset-password'
                                color='primary' 
                                startIcon={<LockResetIcon/>}
                                onClick={() => {
                                    setConfirmTitle('Reset Pensioner Password');
                                    setConfirmMessage("Are you sure you want to reset all these Pensioner's password ?");
                                    showConfirmation('reset');
                                }}
                            >
                                Reset Password
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm="auto">
                            <Button 
                                disabled={selectedRows.length === 0}
                                aria-label='delete'
                                color='error'   
                                startIcon={<DeleteIcon/>}
                                onClick={() => {
                                    setConfirmTitle('Delete Pensioner');
                                    setConfirmMessage("Are you sure you want to delete all these Pensioner's ?");
                                    showConfirmation('delete')
                                }}
                            >
                                Delete
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        );
    }

    const renderConfirmPrompt = () => {
        return (
            <ConfirmPrompt 
                open={confirmVisible}
                title={confirmTitle}
                message={confirmMessage}
                onConfirm={handleConfirmConfirmed}
                onCancel={handleConfirmCancel}
                isLoading={confirmLoading}
            />
        )
    }

    const EmptyState = () => (
        <Box 
            sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '150px',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            backgroundColor: theme.palette.background.paper,
            }}
        >
            <Typography variant="body1" color="text.secondary">
            No payslips found
            </Typography>
        </Box>
    );

    // Loading skeleton for mobile
    const MobileLoadingSkeleton = () => (
        <Box sx={{ mb: 2 }}>
        {[...Array(3)].map((_, index) => (
            <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
                <Box display="flex" justifyContent="flex-end">
                <Skeleton variant="rectangular" width={100} height={36} />
                </Box>
            </CardContent>
            </Card>
        ))}
        </Box>
    );

    if (isMobile) {
        return (
            <>
                {renderheaderView()}  

                <div>
                    {loading ? (
                    <MobileLoadingSkeleton />
                        ) : pensioners.length === 0 ? (
                        <EmptyState />
                        ) : (
                        pensioners.map((pensioner) => (
                            <Card key={pensioner.userId} sx={{ mb: 1 }}>
                                <CardContent>
                                    <Typography variant="subtitle2">
                                    Username: {pensioner.emailAddress.trim() === '' ? pensioner.userName : pensioner.emailAddress}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary">
                                    MEPP No: {pensioner.peopleXDId}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary">
                                    Fullname: {getUserFullName(pensioner)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                    Pay Group: {pensioner.accounts && pensioner.accounts.length > 0 ? pensioner.accounts.map((account: Account) => account.payGroup).join(", ") : "-"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                    Scheme: {pensioner.accounts && pensioner.accounts.length > 0 ? pensioner.accounts.map((account: Account) => account.pensionScheme).join(", ") : "-"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                    Enabled: {pensioner.enabled.toString().toUpperCase()}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                    Locked: {pensioner.locked.toString().toUpperCase()}
                                    </Typography>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {renderActionButtons(pensioner)}
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                    {!loading && pensioners.length > 0 && (
                        <Pager
                            accent='blue'
                            page={page + 1}
                            pageCount={pageCount}
                            onPageRequest={(e) => handlePageChange(e)}
                            style={{ justifyContent: 'center', padding: '5px', borderBottom: '1px solid #e2ecef' }}
                        />
                    )}
                </div>
                
                <SendMessage visible={sendMessageModalVisible} setVisible={setSendMessageModalVisible} />
            
                {renderConfirmPrompt()}
            </>
        );
    }

    return(
        <>
            {renderheaderView()}

            <div style={{ background: '#fff' }}>
                <div style={{ height: 'auto' }}>
                    <DataTable 
                        isLoading={loading}
                        loadingElement={<Span accent="blue">Loading ...</Span>} 
                        accent='blue' 
                        columns={columns} 
                        data={pensioners} 
                    />
                </div>
                <Pager
                    accent='blue'
                    page={page + 1}
                    pageCount={pageCount}
                    onPageRequest={(e) => handlePageChange(e)}
                    style={{ justifyContent: 'center', padding: '5px', borderBottom: '1px solid #e2ecef' }}
                />
            </div>

            <SendMessage visible={sendMessageModalVisible} setVisible={setSendMessageModalVisible} />
            
            {renderConfirmPrompt()}
        </>
    )
}
export default ManagePensioners;